import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  NgControl,
  NgModel,
  UntypedFormControl,
} from '@angular/forms';
import { Observable, pairwise, pipe, UnaryFunction } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  startWith,
} from 'rxjs/operators';
import {
  fromPairs,
  isArray,
  isEmpty,
  isObject,
  isPlainObject,
  cloneDeepWith,
} from 'lodash';
import { avatarColors } from '../constants';
import { ElementRef } from '@angular/core';
import { DateTime } from 'luxon';

export function isControlRequired(control: UntypedFormControl) {
  if (!control) {
    return false;
  }

  if (control.validator) {
    const validator = control.validator({} as AbstractControl);
    if (validator && validator.required) {
      return true;
    }
  }

  return false;
}

export function updateControlValue(control: NgControl, value: number) {
  if (isNgModel(control)) {
    control.valueAccessor.writeValue(value);
    (control as NgModel).update.emit(value);
  } else {
    if (control.control) {
      control.control.setValue(value);
    } else {
      control.reset(value);
    }
  }
}

export function isNgModel(control: NgModel | NgControl): control is NgModel {
  return (control as NgModel).model !== undefined;
}

export function lerpColor(a: string, b: string, amount: number) {
  const ah = parseInt(a.replace(/#/g, ''), 16);
  const ar = ah >> 16;
  const ag = (ah >> 8) & 0xff;
  const ab = ah & 0xff;
  const bh = parseInt(b.replace(/#/g, ''), 16);
  const br = bh >> 16;
  const bg = (bh >> 8) & 0xff;
  const bb = bh & 0xff;
  const rr = ar + amount * (br - ar);
  const rg = ag + amount * (bg - ag);
  const rb = ab + amount * (bb - ab);

  return (
    '#' + (((1 << 24) + (rr << 16) + (rg << 8) + rb) | 0).toString(16).slice(1)
  );
}

export function deepFilter<T extends { [key: string]: any }>(
  data: T[],
  predicate: (item: T) => boolean,
  descendantsFieldName = 'childOptions'
) {
  return !data
    ? null
    : data.reduce((list, entry) => {
        let clone = null;
        if (predicate(entry)) {
          clone = { ...entry };
        } else if (entry[descendantsFieldName]) {
          const children = deepFilter(
            entry[descendantsFieldName],
            predicate,
            descendantsFieldName
          );
          if (children.length > 0) {
            clone = {
              ...entry,
              [descendantsFieldName]: children,
            };
          }
        }

        if (clone) {
          list.push(clone);
        }

        return list;
      }, []);
}

export function deepFilterMap<T extends { [key: string]: any }>(
  data: T[],
  predicate: (item: T) => boolean,
  mapFn: (item: T) => { [key: string]: any },
  alwaysCheckDescendants = false,
  makeClones = true,
  descendantsFieldName = 'childOptions'
) {
  return !data
    ? null
    : data.reduce((list, entry) => {
        let clone: { [key: string]: any } = null;
        if (predicate(entry)) {
          if (makeClones) {
            clone = {
              ...mapFn(entry),
            };
          } else {
            clone = mapFn(entry);
          }

          if (clone) {
            clone[descendantsFieldName] = clone[descendantsFieldName]?.map(
              (desc) => {
                if (makeClones) {
                  return { ...mapFn(desc), _parentRef: clone };
                } else {
                  desc = mapFn(desc);
                  desc._parentRef = clone;
                  return desc;
                }
              }
            );
          }

          if (alwaysCheckDescendants) {
            if (clone) {
              const descendants = deepFilterMap(
                clone[descendantsFieldName],
                predicate,
                mapFn,
                alwaysCheckDescendants,
                makeClones,
                descendantsFieldName
              );

              if (makeClones) {
                clone = {
                  ...clone,
                  [descendantsFieldName]: descendants,
                };
              } else {
                clone[descendantsFieldName] = descendants;
              }
            }
          }
        } else if (entry[descendantsFieldName]) {
          const children = deepFilterMap(
            entry[descendantsFieldName],
            predicate,
            mapFn,
            alwaysCheckDescendants,
            makeClones,
            descendantsFieldName
          );
          if (children.length > 0) {
            clone = makeClones
              ? {
                  ...mapFn(entry),
                }
              : mapFn(entry);

            clone[descendantsFieldName] = children;
          }
        }

        if (clone) {
          list.push(clone);
        }

        return list;
      }, []);
}

export function deepMap<T>(
  options: T[],
  optionCustomizer: (option: T) => T,
  childKey = 'children'
): T[] {
  return options.map((option) => {
    option = optionCustomizer(option);

    if (option[childKey]?.length) {
      option[childKey] = deepMap(option[childKey], optionCustomizer, childKey);
    }

    return option;
  });
}

export function escapeRegExp(string: string) {
  // $& means the whole matched string
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function delayStringSearch(
  minSearchValueLength = 3,
  time = 0
): UnaryFunction<Observable<string>, Observable<string>> {
  return pipe(
    debounceTime(time),
    distinctUntilChanged(),
    startWith(''),
    map((value) => value || ''),
    pairwise(),
    filter(
      ([prevSearchValue, currSearchValue]: [string, string]) =>
        currSearchValue.length >= minSearchValueLength ||
        prevSearchValue.length >= minSearchValueLength
    ),
    map(([, currSearchValue]: [string, string]) =>
      currSearchValue.length >= minSearchValueLength
        ? currSearchValue.toLowerCase()
        : ''
    )
  );
}

export function capitalizeFirstLetter(
  value: string,
  lowercaseRest = false
): string {
  return (
    value.charAt(0).toUpperCase() +
    (lowercaseRest ? value.slice(1).toLowerCase() : value.slice(1))
  );
}

export function transformToArray<T>(value: T | T[]): T[] {
  if (!value) {
    return [];
  }

  if (!Array.isArray(value)) {
    return [value];
  }

  return value;
}

export function stopPropagationEvent<E extends MouseEvent>(e: E) {
  e.stopPropagation();
}

export function preventDefaultEvent<E extends MouseEvent>(e: E) {
  e.preventDefault();
}

export function stopAndPreventEvents<E extends MouseEvent = PointerEvent>(
  e: E
) {
  stopPropagationEvent(e);
  preventDefaultEvent(e);
}

export function transformFreightClass(value: string): string {
  return value && value?.split('_').slice(1).join('.');
}

export function getChangedFormValues(form: FormGroup | FormArray) {
  const changedValues = {};

  Object.keys(form.controls).forEach((key) => {
    const currentControl = form.controls[key];

    switch (true) {
      case currentControl instanceof FormControl:
        checkToSaveControlValue({
          control: currentControl,
          saveRef: changedValues,
          key,
        });
        break;
      case currentControl instanceof FormGroup:
        changedValues[key] = getChangedFormValues(currentControl as FormGroup);
        break;
      case currentControl instanceof FormArray:
        changedValues[key] = [];
        currentControl.controls.forEach((control) => {
          if (control instanceof FormControl) {
            checkToSaveControlValue({
              control: control,
              saveRef: changedValues,
              key,
            });
          } else {
            const changedArrayValues = getChangedFormValues(
              control as FormGroup | FormArray
            );
            if (Object.keys(changedArrayValues).length) {
              changedValues[key].push(changedArrayValues);
            }
          }
        });
        break;
    }
  });

  return changedValues;
}

export function checkToSaveControlValue({
  control,
  saveRef,
  key,
}: {
  control: FormControl | FormArray;
  saveRef: object;
  key: string;
}) {
  if (control.dirty) {
    saveRef[key] = control.value;
  }
}

export function getDirtyFormValues(form: FormGroup | FormArray) {
  return removeEmptyObjects(getChangedFormValues(form));
}

export function markChangedControlsAsDirty(
  values: { [key: string]: any },
  form: FormGroup
) {
  Object.keys(values).forEach((key) => {
    form.get(key).markAsDirty();
  });
}

export function removeEmptyObjects(element: any) {
  if (!isPlainObject(element)) {
    return element;
  }

  if (isArray(element)) {
    return element.map(removeEmptyObjects);
  }

  return fromPairs(
    Object.entries(element)
      .map(([k, v]) => [k, removeEmptyObjects(v)])
      .filter(([, v]) => !(isObject(v) && isEmpty(v)))
  );
}

export function getAvatarColor(value: string) {
  const numericValue = numberFromString(value || '');
  const index = Number.isFinite(numericValue)
    ? numericValue % avatarColors.length
    : 0;
  return `var(--twc-${avatarColors[index]})`;
}

export function numberFromString(string: string): number {
  const charCodes = string
    .split('')
    .map((char) => char.charCodeAt(0))
    .join('');
  return parseInt(charCodes, 10);
}

export function formControlsComparator(
  form: FormGroup | FormArray,
  comparator: (control: FormControl) => void
) {
  Object.keys(form.controls).forEach((key) => {
    const currentControl = form.controls[key];

    switch (true) {
      case currentControl instanceof FormControl:
        comparator(currentControl);
        break;
      case currentControl instanceof FormGroup:
        formControlsComparator(currentControl as FormGroup, comparator);
        break;
      case currentControl instanceof FormArray:
        currentControl.controls.forEach((control) => {
          if (control instanceof FormControl) {
            comparator(control);
          } else {
            formControlsComparator(
              control as FormGroup | FormArray,
              comparator
            );
          }
        });
        break;
    }
  });
}

export function isEmptyValue(value: any): boolean {
  switch (true) {
    case typeof value === 'number':
      return value === null || value === undefined;
    case typeof value === 'string':
      return (
        value === null || value === undefined || (value as string).length === 0
      );
    case value instanceof Array:
    case typeof value === 'object':
      return isEmpty(value);
    default:
      return value === null || value === undefined;
  }
}

export function transformEnumToName(value: string): string {
  value = value.toLowerCase();
  value = capitalizeFirstLetter(value);
  value = value.split('_').join(' ');
  return value;
}

export function getIDMap<T>(entries: T[]): number[] {
  return entries?.map((entry: T & { id: number }) => entry.id);
}

export function getDeletedEntries<E, I>(
  entries: E[],
  initialEntries: I[]
): number[] {
  let changedEntries = [];

  if (initialEntries?.length) {
    const initialIDs = getIDMap(initialEntries);
    const entryIDs = getIDMap(entries);
    changedEntries = initialIDs.filter((id: number) => !entryIDs.includes(id));
  }

  return changedEntries;
}

export function getAddedEntries<E, I>(
  entries: E[],
  initialEntries: I[]
): number[] {
  const entryIDs = getIDMap(entries);
  let changedEntriesIDs = entryIDs;

  if (initialEntries?.length) {
    const initialIDs = getIDMap(initialEntries);
    changedEntriesIDs = entryIDs?.filter(
      (id: number) => !initialIDs.includes(id)
    );
  }

  return changedEntriesIDs;
}

export function getNumberWithoutSpecialChars(
  number: string
): string | undefined {
  return number?.replace(/[() -]/g, '');
}

export function prependPlusToNumber(number: string): string | undefined {
  return number.startsWith('00') ? `+${number.slice(2)}` : number;
}

export function scrollToAnchor(scrollContainer: string, anchor: string) {
  const scrollableContainer = document.querySelector(`.${scrollContainer}`);
  const element = document.getElementById(anchor);
  if (scrollableContainer && element) {
    const containerRect = scrollableContainer.getBoundingClientRect();
    const targetRect = element.getBoundingClientRect();
    const scrollToY =
      targetRect.top - containerRect.top + scrollableContainer.scrollTop;

    /**
     * Scroll to clicked fragment
     */
    scrollableContainer.scrollTo({ top: scrollToY, behavior: 'smooth' });
  }
}

export function getAsString(value: string | number | null): string {
  return `${value}`;
}

export function getPercentageColor(value: number): string {
  switch (true) {
    case value === 0:
      return '#EBECEC';
    case value > 0 && value <= 50:
      return '#F24B40';
    case value > 50 && value <= 75:
      return '#FFA600';
    case value > 75 && value <= 90:
      return '#0059FF';
    case value > 90 && value <= 100:
      return '#22B54C';
    default:
      return '#22B54C';
  }
}

export function getPercentage(value1: number, value2: number) {
  if (value1 === 0) {
    return 0;
  }

  if (value2 === 0 && value1 > 0) {
    return 100;
  }

  return (value1 / value2) * 100;
}

export function getColorFromValue(value1: number, value2: number) {
  return getPercentageColor(getPercentage(value1, value2));
}

export function exportTable(elementRef: ElementRef, fileName: string): void {
  const excludedColumns = ['mat-column-actions', 'mat-column-select'];

  if (!elementRef || !elementRef.nativeElement) {
    console.error('Invalid ElementRef provided.');
    return;
  }
  const headerRow = elementRef.nativeElement.querySelector('mat-header-row');
  const headerCells = headerRow
    ? (Array.from(
        headerRow.querySelectorAll('mat-header-cell')
      ) as HTMLElement[])
    : [];
  const headers = headerCells
    .filter((cell) =>
      Array.from(cell.classList).every(
        (className) => !excludedColumns.includes(className)
      )
    )
    .map((cell) => `"${cell.innerText.replace(/"/g, '""')}"`);

  if (headers.length === 0) {
    console.warn('No headers found in the table.');
    return;
  }

  const dataRows = Array.from(
    elementRef.nativeElement.querySelectorAll('mat-row')
  ) as HTMLElement[];

  const checkedRows = dataRows.filter((row) => {
    const checkbox = row.querySelector(
      'mat-cell mat-checkbox input[type="checkbox"]'
    );
    return checkbox && (checkbox as HTMLInputElement).checked;
  });

  if (checkedRows.length === 0) {
    console.warn('No checked rows found for export.');
    return;
  }

  const csvData: string[] = checkedRows.map((row) => {
    const cells = Array.from(
      row.querySelectorAll('mat-cell, mat-header-cell')
    ) as HTMLElement[];
    const excludedCellElementsSelectors = [
      '.indicators',
      'nx-open-notes-button',
    ];

    const rowData = cells
      .filter((cell) =>
        Array.from(cell.classList).every(
          (className) => !excludedColumns.includes(className)
        )
      )
      .map((cell) => {
        const cellClone = cell.cloneNode(true) as HTMLElement;
        cellClone
          .querySelectorAll(excludedCellElementsSelectors.join(','))
          .forEach((el) => el.remove());
        return `"${cellClone.innerText.replace(/"/g, '""')}"`;
      });

    return rowData.join(',');
  });

  downloadCSVFile([headers.join(','), ...csvData].join('\r\n'), fileName);
}

export function downloadCSVFile(csvContent: string, fileName: string): void {
  if (!csvContent || !fileName) {
    console.error('Invalid content or file name for download.');
    return;
  }
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.setAttribute('download', `${fileName}.csv`);
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.URL.revokeObjectURL(url);
}

export function getDateTimeOffsetName(date: DateTime): string {
  return date.offsetNameLong
    ?.split(' ')
    .map((name) => name.charAt(0))
    .join('');
}

export function getOrdinal(value: number): string {
  if (!value || isNaN(value)) return '';

  const suffixes = ['th', 'st', 'nd', 'rd'];
  const v = value % 100;

  return suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0];
}

export function getIntegerParam(
  value: string,
  failCallback: () => void = () => {
    location.assign('/');
  }
) {
  const param = parseInt(value, 10);
  if (Number.isFinite(param)) {
    return param;
  } else {
    if (typeof failCallback === 'function') {
      failCallback();
    }
    return null;
  }
}

export function transformStringToMask(value: string): string {
  return value.replace(/[a-zA-Z]/g, '*');
}

export function convertAllToStrings<T, R>(arr: T[]): R[] {
  return cloneDeepWith(arr, (value) => {
    if (typeof value === 'number') {
      return value.toString();
    }

    return undefined;
  });
}

export function camelToSnake(input: string): string {
  return input
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/([A-Z])([A-Z][a-z])/g, '$1_$2')
    .toLowerCase();
}
